import type { DataObjectSummary, SchemaFieldSummary } from "./types";

type FilterOp =
    | "equals"
    | "notEquals"
    | "contains"
    | "startsWith"
    | "endsWith"
    | "gt"
    | "gte"
    | "lt"
    | "lte"
    | "between"
    | "in"
    | "isEmpty"
    | "isNotEmpty";

export interface FilterCriterion {
    field: string;
    op: FilterOp;
    value?: unknown;
}

function resolveValue(record: DataObjectSummary, fieldName: string, schemaFields: SchemaFieldSummary[]): unknown {
    const field = schemaFields.find((f) => (f?.name || "").toLowerCase() === (fieldName || "").toLowerCase());
    if (field && field.fieldId && Object.prototype.hasOwnProperty.call(record, field.fieldId)) {
        return (record as any)[field.fieldId];
    }
    if (Object.prototype.hasOwnProperty.call(record, fieldName)) {
        return (record as any)[fieldName];
    }
    return undefined;
}

function asString(v: unknown): string {
    if (v === null || v === undefined) return "";
    return String(v);
}

function asNumber(v: unknown): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function evaluate(criterion: FilterCriterion, recordValue: unknown): boolean {
    const { op, value } = criterion;
    switch (op) {
        case "equals":
            return asString(recordValue).trim().toLowerCase() === asString(value).trim().toLowerCase();
        case "notEquals":
            return asString(recordValue).trim().toLowerCase() !== asString(value).trim().toLowerCase();
        case "contains":
            return asString(recordValue).toLowerCase().includes(asString(value).toLowerCase());
        case "startsWith":
            return asString(recordValue).toLowerCase().startsWith(asString(value).toLowerCase());
        case "endsWith":
            return asString(recordValue).toLowerCase().endsWith(asString(value).toLowerCase());
        case "gt":
        case "gte":
        case "lt":
        case "lte": {
            const a = asNumber(recordValue);
            const b = asNumber(value);
            if (a === null || b === null) return false;
            if (op === "gt") return a > b;
            if (op === "gte") return a >= b;
            if (op === "lt") return a < b;
            return a <= b;
        }
        case "between": {
            if (!Array.isArray(value) || value.length !== 2) return false;
            const a = asNumber(recordValue);
            const lo = asNumber(value[0]);
            const hi = asNumber(value[1]);
            if (a === null || lo === null || hi === null) return false;
            return a >= lo && a <= hi;
        }
        case "in": {
            if (!Array.isArray(value)) return false;
            const target = asString(recordValue).trim().toLowerCase();
            return value.some((v) => asString(v).trim().toLowerCase() === target);
        }
        case "isEmpty":
            return asString(recordValue).trim() === "";
        case "isNotEmpty":
            return asString(recordValue).trim() !== "";
        default:
            return false;
    }
}

export function filterDataObjects(
    dataObjects: DataObjectSummary[],
    schemaFields: SchemaFieldSummary[],
    criteria: FilterCriterion[],
): DataObjectSummary[] {
    if (!Array.isArray(dataObjects) || !Array.isArray(criteria) || criteria.length === 0) return [];
    return dataObjects.filter((record) =>
        criteria.every((c) => {
            const v = resolveValue(record, c.field, schemaFields);
            return evaluate(c, v);
        }),
    );
}


export interface ErroredRecordMatch {
    dataObjectId: string;
    // Human-readable per-field reasons, e.g. [{ field: "email", reasons: ["must be a valid email"] }].
    errors: Array<{ field: string; reasons: string[] }>;
}

export function filterErroredDataObjects(
    dataObjects: DataObjectSummary[],
    schemaFields: SchemaFieldSummary[],
    fieldName?: string,
): ErroredRecordMatch[] {
    if (!Array.isArray(dataObjects)) return [];
    const idToName = new Map<string, string>();
    for (const f of schemaFields || []) {
        if (f?.fieldId && f?.name) idToName.set(f.fieldId, f.name);
    }
    const targetField = (fieldName || "").trim().toLowerCase();

    const matches: ErroredRecordMatch[] = [];
    for (const record of dataObjects) {
        const rawErrors = (record as any).errors;
        if (!Array.isArray(rawErrors) || rawErrors.length === 0) continue;

        const errors: Array<{ field: string; reasons: string[] }> = [];
        for (const e of rawErrors) {
            const name = idToName.get(e?.name) || e?.name || "record";
            const reasons = Array.isArray(e?.errors) ? e.errors.map((r: unknown) => String(r)) : e?.errors ? [String(e.errors)] : [];
            if (targetField && name.toLowerCase() !== targetField) continue;
            errors.push({ field: name, reasons });
        }
        if (errors.length > 0) {
            matches.push({ dataObjectId: record.dataObjectId, errors });
        }
    }
    return matches;
}

// --- similarity ---

function normaliseToken(s: string): string {
    return s.trim().toLowerCase();
}

function levenshtein(a: string, b: string): number {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) dp[i][0] = i;
    for (let j = 0; j <= b.length; j++) dp[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
            dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
        }
    }
    return dp[a.length][b.length];
}

function tokenSimilarity(a: string, b: string): number {
    const sa = normaliseToken(a);
    const sb = normaliseToken(b);
    if (sa === "" && sb === "") return 1;
    if (sa === "" || sb === "") return 0;
    if (sa === sb) return 1;
    const dist = levenshtein(sa, sb);
    return 1 - dist / Math.max(sa.length, sb.length);
}

export interface SimilarityMatch {
    dataObjectId: string;
    score: number;
}

export function findSimilarRecords(
    dataObjects: DataObjectSummary[],
    schemaFields: SchemaFieldSummary[],
    reference: Record<string, unknown>,
    fields: string[],
    minScore: number,
    excludeId?: string,
): SimilarityMatch[] {
    if (!Array.isArray(dataObjects) || !Array.isArray(fields) || fields.length === 0) return [];
    const refStrings = fields.map((f) => asString(reference[f] ?? reference[f.toLowerCase()]));
    const matches: SimilarityMatch[] = [];
    for (const record of dataObjects) {
        if (excludeId && record.dataObjectId === excludeId) continue;
        let totalScore = 0;
        let counted = 0;
        for (let i = 0; i < fields.length; i++) {
            const fieldName = fields[i];
            const recordValue = resolveValue(record, fieldName, schemaFields);
            const score = tokenSimilarity(refStrings[i], asString(recordValue));
            totalScore += score;
            counted += 1;
        }
        const avg = counted > 0 ? totalScore / counted : 0;
        if (avg >= minScore && avg < 1) {
            matches.push({ dataObjectId: record.dataObjectId, score: avg });
        }
    }
    matches.sort((a, b) => b.score - a.score);
    return matches.slice(0, 50);
}
